import { MySection } from '@/components/custom/my-section';
import { BulkReceiveModal } from '@/components/products/BulkReceiveModal';
import { Button } from '@/components/ui/button';
import { useBulkReceiveStock } from '@/hooks/product/use-bulk-receive-stock';
import { useVariantEdit } from '@/hooks/product/use-variant-edit';
import { toast } from '@/hooks/use-toast';
import { useProductWithRelation } from '@/services/product-service';
import {
  useCreateProductVariant,
  useDeleteProductVariant,
} from '@/services/product-variant-service';
import { ProductFormValues, ProductUpdateValues, ProductVariant } from '@/types/product-type';
import {
  ProductVariantCreateFormValues,
} from '@/types/product-variant-type';
import { Grid } from '@mui/material';
import Box from '@mui/system/Box';
import { Layers, PackagePlus } from 'lucide-react';
import { useState } from 'react';
import { Control, UseFormWatch } from 'react-hook-form';
import { useParams } from 'wouter';
import { ProductAttributesFormSection } from './product-attributes-form-section';
import { AddVariantButton } from './section/variant/add-variant-button';
import { CreateVariantDialog } from './section/variant/create-variant-dialog';
import { EditVariantDialog } from './section/variant/edit-variant-dialog';
import { EmptyVariantsState } from './section/variant/empty-variants-state';
import { LoadingVariantsState } from './section/variant/loading-variants-state';
import { VariantsTable } from './section/variant/variants-table';

type ProductFormMode = 'create' | 'update';

interface ProductVariantFormSectionProps {
  watch: UseFormWatch<ProductFormValues | ProductUpdateValues>;
  mode: ProductFormMode;
  control: Control<ProductFormValues | ProductUpdateValues>;
}

export function ProductVariantFormSection({
  watch,
  mode,
  control,
}: ProductVariantFormSectionProps) {
  const params = useParams();
  const productId = params.id as string | undefined;
  const productCode = watch('code' as any) as string | undefined;

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showBulkReceiveModal, setShowBulkReceiveModal] = useState(false);

  // Fetch product data with relations when productId exists
  const {
    data: productWithRelation,
    isLoading: isLoadingProduct,
    refresh,
  } = useProductWithRelation(productId || null);

  const productAttributes = productWithRelation?.productAttributes || [];
  const productVariants = productWithRelation?.productVariants || [];

  const createMutation = useCreateProductVariant();
  const deleteMutation = useDeleteProductVariant();

  // Use the variant edit hook
  const {
    isEditDialogOpen,
    setIsEditDialogOpen,
    editingVariant,
    isUpdating,
    handleEdit,
    handleEditSubmit,
    handleCancel,
  } = useVariantEdit({
    productId: productId || '',
    onSuccess: refresh,
  });

  const handleCreateSubmit = async (data: ProductVariantCreateFormValues) => {
    if (!productId) {
      toast({
        title: 'Error',
        description: 'Product must be saved before adding variants',
        variant: 'destructive',
      });
      return;
    }

    setIsCreating(true);
    try {
      const response = await createMutation.executeAsync(data);
      if (response.success) {
        toast({
          title: 'Success',
          description: 'Variant created successfully',
        });
        setIsCreateDialogOpen(false);
        refresh();
      } else {
        toast({
          title: 'Error',
          description: response.message || 'Failed to create variant',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create variant',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (variant: ProductVariant) => {
    if (!productId) return;

    try {
      await deleteMutation.executeAsync({
        id: variant.id,
        productId,
      });
      toast({
        title: 'Success',
        description: 'Variant deleted successfully',
      });
      refresh();
    } catch (error) {
      console.error('Error deleting variant:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete variant',
        variant: 'destructive',
      });
    }
  };

  const { handleBulkReceiveStock } = useBulkReceiveStock({
    onSuccess: () => {
      refresh();
    },
  });

  // Handle create mode - productId doesn't exist yet
  if (!productId) {
    // In create mode, show attributes form instead of "save first" message
    if (mode === 'create') {
      return <ProductAttributesFormSection control={control} />;
    }

    // In update mode without productId, show "save first" message
    return (
      <MySection title="Variants">
        <Grid container gap={2}>
          <Grid item xs={12}>
            <div className="text-center py-8">
              <Layers className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                Variants can be added after product is saved
              </h3>
              <p className="text-muted-foreground text-sm">
                Save the product first, then you can add and manage variants.
              </p>
            </div>
          </Grid>
        </Grid>
      </MySection>
    );
  }

  // Loading state
  if (isLoadingProduct) {
    return (
      <MySection title="Variants">
        <Grid container gap={2}>
          <Grid item xs={12}>
            <LoadingVariantsState />
          </Grid>
        </Grid>
      </MySection>
    );
  }

  return (
    <MySection
      title="Variants"
      description="Manage product variants with different attributes and pricing"
      action={
        <Box className="flex items-center gap-2">
          <Button variant="outline" type="button" onClick={() => setShowBulkReceiveModal(true)}>
            <PackagePlus className="mr-2 h-4 w-4" />
            Bulk Stock Receive
          </Button>
          <AddVariantButton onClick={() => setIsCreateDialogOpen(true)} />
        </Box>
      }
    >
      <Grid container gap={2}>
        <Grid item xs={12}>
          {productVariants.length > 0 ? (
            <VariantsTable variants={productVariants} onEdit={handleEdit} onDelete={handleDelete} />
          ) : (
            <EmptyVariantsState onAddVariant={() => setIsCreateDialogOpen(true)} />
          )}
        </Grid>
      </Grid>

      {/* Create Dialog */}
      <CreateVariantDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        productId={productId}
        productCode={productCode || ''}
        productAttributes={productAttributes}
        onSubmit={handleCreateSubmit}
        isLoading={isCreating}
      />

      {/* Edit Dialog */}
      {productId && (
        <EditVariantDialog
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          editingVariant={editingVariant}
          productId={productId}
          productCode={productCode || ''}
          onSubmit={handleEditSubmit}
          onCancel={handleCancel}
          isLoading={isUpdating}
        />
      )}

      {/* Bulk Receive Modal */}
      {productId && (
        <BulkReceiveModal
          isOpen={showBulkReceiveModal}
          productId={productId}
          onSubmit={handleBulkReceiveStock}
          onCancel={() => setShowBulkReceiveModal(false)}
        />
      )}
    </MySection>
  );
}
